/*******************************************************************************
localStorage: browser localStorage wrapper

Notice that HTTP and HTTPS sites have their own sets of localStorage. We cannot
share them, and we shouldn't, as mixed content (HTTP and HTTPS resources) on
one site causes problems because of browser security restrictions.
*******************************************************************************/

import 'operative'; // Defines window.operative
import LZString from 'lz-string';

import webApp from './webApp';

let browserSupportsLocalStorage = false;
let lzstringWorker = null;

// These functions are stringified by `operative`. Therefore these functions
// can only refer to variables and functions that exist in the web worker's
// scope. You can pass dependencies to the web worker via the `operative()`
// call below.
const lzStringFunctionsForWebWorker = {
  compressToUTF16: function( str, callback ) {
    // Note: here `LZString` comes from the worker's global scope, and not from
    // the scope of the current .js file. The worker loads `js/lz-string.min.js`
    // into the worker's scope.
    callback( self.LZString.compressToUTF16( str ) );
  },
  decompressFromUTF16: function( str, callback ) {
    // Note: here `LZString` comes from the worker's global scope, and not from
    // the scope of the current .js file. The worker loads `js/lz-string.min.js`
    // into the worker's scope.
    callback( self.LZString.decompressFromUTF16( str ) );
  }
};

const localStorage = {

  init() {
    let hasLocalStorage = false;

    // If you disable cookies in Chrome or FF, accessing window.localStorage throws an error
    try {
      hasLocalStorage = ( typeof window.localStorage === 'object'
        && typeof window.localStorage.getItem === 'function' );
    }
    catch( e ) {}

    if ( !hasLocalStorage )
      return;

    browserSupportsLocalStorage = true;

    // We use operative.js for abstracting Web Worker functionality
    lzstringWorker = window.operative( lzStringFunctionsForWebWorker, [
      // We need `LZString` inside the web worker's scope
      webApp.env.plugin_url +'/customer-apps/web-app/js/lz-string.min.js'
    ] );
  },

  isAvailable: function() {
    return browserSupportsLocalStorage;
  },

  hasSpace: function() {

    if ( !browserSupportsLocalStorage )
      return false;

    var ret = false;
    try {
      window.localStorage.setItem( "_asteikko", "m" );
      ret = true;
    }
    catch(e) {}
    try {
      window.localStorage.removeItem( "_asteikko" );
    }
    catch(e) {}
    return ret;
  },

  has: function( name ) {

    if ( !browserSupportsLocalStorage )
      return false;

    return window.localStorage.hasOwnProperty( name ) &&
      window.localStorage.getItem( name ) !== null;
  },

  get: function( name ) {

    if ( !browserSupportsLocalStorage )
      return null;

    try {
      var value = window.localStorage.getItem( name );
      if ( value && value.substr( 0, 4 ) === 'lzs|' )
        return LZString.decompressFromUTF16( value.substr( 4 ) );
      else
        return value;
    }
    catch ( e ) {
      webApp.console.error( e );
      return null;
    }
  },

  getAsync: function( name, callback ) {

    if ( typeof callback !== 'function' ) {
      webApp.console.error( 'Calling getAsync() without a callback is a no-op' );
      return;
    }

    if ( !browserSupportsLocalStorage ) {
      callback( null );
      return;
    }

    try {
      var value = window.localStorage.getItem( name );
    }
    catch ( e ) {
      webApp.console.error( e );
      callback( null );
      return;
    }

    if ( value && value.substr( 0, 4 ) === 'lzs|' ) {
      lzstringWorker.decompressFromUTF16( value.substr( 4 ), function( ret ) {
        callback( ret );
      } );
    }
    else {
      callback( value );
    }
  },

  set: function( name, value ) {

    if ( !browserSupportsLocalStorage )
      return false;

    value = String( value );

    try {
      window.localStorage.setItem( name, 'lzs|'+ LZString.compressToUTF16( value ) );
      return true;
    }
    catch( e ) {
      webApp.console.error( e );
      return false;
    }
  },

  setAsync: function( name, value, callback ) {

    if ( !browserSupportsLocalStorage ) {
      if ( typeof callback === 'function' )
        callback( false );
      return;
    }

    value = String( value );

    lzstringWorker.compressToUTF16( value, function( compressed ) {

      try {
        window.localStorage.setItem( name, 'lzs|'+ compressed );
        var ret = true;
      }
      catch ( e ) {
        var ret = false;
      }

      if ( typeof callback === 'function' )
        callback( ret );
    } );
  },

  remove: function( name ) {

    if ( !browserSupportsLocalStorage )
      return false;

    try {
      window.localStorage.removeItem( name );
      return true;
    }
    catch( e ) {
      webApp.console.error( e );
      return false;
    }
  },

  clear: function() {

    if ( !browserSupportsLocalStorage )
      return false;

    for ( var prop in window.localStorage ) {
      if ( window.localStorage.hasOwnProperty( prop ) ) {
        try {
          window.localStorage.removeItem( prop );
        }
        catch( e ) {
          webApp.console.error( e );
        }
      }
    }
    return true;
  }
};

export default localStorage;
